import React from 'react';
import { FaCheckCircle, FaLightbulb, FaEye, FaBullseye } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();
  
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-black-900 via-maroon-900 to-brown-900 text-white py-24">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">{t('about.page.hero.title')}</h1>
            <p className="text-2xl text-gray-200 leading-relaxed">
              {t('about.page.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="max-w-5xl mx-auto">
            <div className="inline-block px-4 py-2 bg-maroon-100 rounded-lg mb-8">
              <span className="text-maroon-700 font-bold text-sm uppercase tracking-wider">{t('about.page.whoWeAre.badge')}</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-black-900 mb-8">
              {t('about.page.whoWeAre.title')} <span className="text-maroon-700">{t('about.page.whoWeAre.titleHighlight')}</span>
            </h2>
            
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                {t('about.page.whoWeAre.intro1')}
              </p>
              
              <p className="font-semibold text-xl text-maroon-700">
                {t('about.page.whoWeAre.intro2')}
              </p>
              
              <div className="bg-gradient-to-br from-gold-50 to-maroon-50 rounded-2xl p-8 border-l-4 border-gold-500 my-8">
                <h3 className="text-2xl font-bold text-maroon-900 mb-4">{t('about.page.whoWeAre.whatIs.title')}</h3>
                <p>
                  {t('about.page.whoWeAre.whatIs.description')}
                </p>
                <p className="mt-4">
                  {t('about.page.whoWeAre.whatIs.service')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 my-10">
                <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-blue-100 hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white font-bold text-xl">SBLC</span>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">{t('about.page.whoWeAre.sblc.title')}</h4>
                  </div>
                  <p className="text-gray-700">
                    {t('about.page.whoWeAre.sblc.description')}
                  </p>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-green-100 hover:shadow-2xl transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4">
                      <span className="text-white font-bold text-xl">BG</span>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">{t('about.page.whoWeAre.bg.title')}</h4>
                  </div>
                  <p className="text-gray-700">
                    {t('about.page.whoWeAre.bg.description')}
                  </p>
                </div>
              </div>

              <p>
                {t('about.page.whoWeAre.simplify')}
              </p>

              <p>
                {t('about.page.whoWeAre.partner')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise - Bankable Projects */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-gold-100 rounded-lg mb-6">
                <span className="text-maroon-700 font-bold text-sm uppercase tracking-wider">{t('about.page.expertise.badge')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-black-900 mb-6">
                {t('about.page.expertise.title')} <span className="text-maroon-700">{t('about.page.expertise.titleHighlight')}</span>
              </h2>
            </div>

            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                {t('about.page.expertise.intro')}
              </p>

              <p>
                {t('about.page.expertise.process')}
              </p>

              <div className="bg-gradient-to-br from-maroon-50 to-brown-50 rounded-2xl p-8 border-l-4 border-maroon-500 my-8">
                <h3 className="text-2xl font-bold text-maroon-900 mb-4">{t('about.page.expertise.bankable.title')}</h3>
                <p>
                  {t('about.page.expertise.bankable.description')}
                </p>
                <p className="mt-4">
                  {t('about.page.expertise.belief')}
                </p>
              </div>

              <p>
                {t('about.page.values.integrity')}
              </p>

              <p className="text-xl font-semibold text-maroon-700 bg-gold-50 p-6 rounded-xl border-l-4 border-gold-500">
                {t('about.page.values.education')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Network & Process */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-maroon-100 rounded-lg mb-6">
                <span className="text-maroon-700 font-bold text-sm uppercase tracking-wider">{t('about.page.network.title')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-black-900 mb-6">
                {t('about.page.network.title')}
              </h2>
            </div>

            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                {t('about.page.network.intro')}
              </p>

              <div className="grid md:grid-cols-3 gap-6 my-10">
                <div className="bg-gradient-to-br from-maroon-50 to-white rounded-xl shadow-lg p-6 border-2 border-maroon-100 hover:shadow-2xl transition-all duration-300">
                  <div className="text-3xl font-bold text-maroon-700 mb-3">📋</div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Deed of Agreement</h4>
                  <p className="text-gray-600 text-sm">Legally binding contracts ensuring clarity and commitment</p>
                </div>

                <div className="bg-gradient-to-br from-gold-50 to-white rounded-xl shadow-lg p-6 border-2 border-gold-100 hover:shadow-2xl transition-all duration-300">
                  <div className="text-3xl font-bold text-gold-600 mb-3">📝</div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Purchase Contract</h4>
                  <p className="text-gray-600 text-sm">Structured agreements for international transactions</p>
                </div>

                <div className="bg-gradient-to-br from-brown-50 to-white rounded-xl shadow-lg p-6 border-2 border-brown-100 hover:shadow-2xl transition-all duration-300">
                  <div className="text-3xl font-bold text-brown-600 mb-3">💰</div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Payment Undertaking</h4>
                  <p className="text-gray-600 text-sm">Financial commitments creating a legally sound foundation</p>
                </div>
              </div>

              <p>
                {t('about.page.network.docs')}
              </p>

              <p>
                {t('about.page.network.clients')}
              </p>

              <div className="bg-gradient-to-r from-maroon-700 to-brown-800 text-white rounded-2xl p-10 my-10">
                <h3 className="text-3xl font-bold mb-4">Efficiency & Compliance</h3>
                <p className="text-xl leading-relaxed">
                  {t('about.page.efficiency')}
                </p>
              </div>

              <p className="text-xl font-semibold text-center text-maroon-700 bg-gold-50 p-8 rounded-2xl">
                {t('about.page.success')}
              </p>

              <p className="text-lg text-center">
                {t('about.page.commitment')}
              </p>

              <div className="text-center mt-12 pt-12 border-t-2 border-gold-200">
                <p className="text-2xl font-bold text-maroon-900 mb-4">Our Mission:</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-maroon-700 via-gold-600 to-brown-700 bg-clip-text text-transparent leading-tight">
                  Bridge Trust • Empower Finance • Realize Growth
                </p>
                <p className="text-xl text-gray-600 mt-4 italic">— One Project at a Time —</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Vision */}
            <div className="bg-gradient-to-br from-maroon-700 to-maroon-900 rounded-2xl shadow-2xl p-10 text-white">
              <div className="flex items-center mb-6">
                <div className="bg-gold-400 rounded-full p-4 mr-4">
                  <FaEye className="text-3xl text-maroon-900" />
                </div>
                <h2 className="text-3xl font-heading font-bold">Our Vision</h2>
              </div>
              <p className="text-lg text-gray-100 leading-relaxed">
                To be the leading <strong>Bank Instrument Consultancy</strong> in Southeast Asia, recognized globally for bridging entrepreneurial vision with international-grade financial solutions, enabling ambitious projects to access the capital they deserve.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-gradient-to-br from-gold-500 to-gold-700 rounded-2xl shadow-2xl p-10 text-white">
              <div className="flex items-center mb-6">
                <div className="bg-maroon-900 rounded-full p-4 mr-4">
                  <FaBullseye className="text-3xl text-gold-300" />
                </div>
                <h2 className="text-3xl font-heading font-bold">Our Mission</h2>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <FaCheckCircle className="text-maroon-900 mr-3 mt-1 flex-shrink-0 text-xl" />
                  <span className="text-gray-100 leading-relaxed"><strong>Bridge Trust:</strong> Build authentic, verified financial partnerships</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-maroon-900 mr-3 mt-1 flex-shrink-0 text-xl" />
                  <span className="text-gray-100 leading-relaxed"><strong>Empower Finance:</strong> Provide access to SBLC, BG, and global funding</span>
                </li>
                <li className="flex items-start">
                  <FaCheckCircle className="text-maroon-900 mr-3 mt-1 flex-shrink-0 text-xl" />
                  <span className="text-gray-100 leading-relaxed"><strong>Realize Growth:</strong> Transform bankable projects into funded realities</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-16 xl:px-24">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-gold-100 rounded-lg mb-6">
              <span className="text-maroon-700 font-bold text-sm uppercase tracking-wider">Our Foundation</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-black-900 mb-6">
              Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that define our commitment to excellence
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <ValueCard 
              icon={<FaCheckCircle />}
              title="Integrity"
              description="Trust is not granted—it is built through consistent, ethical practice and transparency in every transaction"
            />
            <ValueCard 
              icon={<FaLightbulb />}
              title="Education"
              description="We ensure clients understand the 'what' and the 'why' behind every financial strategy and instrument"
            />
            <ValueCard 
              icon={<FaEye />}
              title="Clarity"
              description="Transforming complex financial structures into understandable and actionable strategies for our clients"
            />
            <ValueCard 
              icon={<FaBullseye />}
              title="Excellence"
              description="International expertise combined with personalized approach to deliver verified, authentic, and enforceable solutions"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const ValueCard = ({ icon, title, description }) => {
  return (
    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-md p-6 text-center hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-gold-400">
      <div className="text-4xl text-maroon-700 mb-4 flex justify-center">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

export default About;
